import React, { useRef, useEffect, useState, Suspense } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import CCTVStream from './Selfie';

const Camera3D = () => {
    const containerRef = useRef(null);
    const [shouldMount, setShouldMount] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isSelfieOpen, setIsSelfieOpen] = useState(false);
    const unmountTimerRef = useRef(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // Cancel any pending unmount
                    if (unmountTimerRef.current) {
                        clearTimeout(unmountTimerRef.current);
                        unmountTimerRef.current = null;
                    }
                    setShouldMount(true);
                } else {
                    // Unmount after delay to free WebGL context
                    unmountTimerRef.current = setTimeout(() => {
                        setShouldMount(false);
                        setIsLoaded(false);
                    }, 3000);
                }
            },
            {
                threshold: 0.05,
                rootMargin: '0px' // No preloading - only when actually visible
            }
        );

        observer.observe(container);
        return () => {
            observer.disconnect();
            if (unmountTimerRef.current) {
                clearTimeout(unmountTimerRef.current);
            }
        };
    }, []);

    return (
        <>
            <div
                ref={containerRef}
                className="absolute inset-0 w-full h-full cursor-pointer group"
                onClick={() => setIsSelfieOpen(true)}
            >
                {shouldMount && !isLoaded && <Loader />}
                {shouldMount && (
                    <Canvas
                        shadows
                        dpr={[1, 1.5]}
                        gl={{
                            alpha: true,
                            antialias: true,
                            powerPreference: 'high-performance',
                            toneMapping: THREE.NoToneMapping,
                        }}
                        style={{ width: '100%', height: '100%' }}
                    >
                        <Suspense fallback={null}>
                            <SceneSetup onLoaded={() => setIsLoaded(true)} />
                        </Suspense>
                    </Canvas>
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-blue-500/0 to-transparent opacity-0 group-hover:opacity-10 transition-opacity duration-500 pointer-events-none" />
            </div>

            <CCTVStream
                isOpen={isSelfieOpen}
                onClose={() => setIsSelfieOpen(false)}
            />
        </>
    );
};

function Loader() {
    return (
        <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-12 h-12">
                <div className="absolute inset-0 border-2 border-white/10 border-t-white/40 rounded-full animate-spin"></div>
            </div>
        </div>
    );
}

function SceneSetup({ onLoaded }) {
    const { camera } = useThree();
    const gltf = useGLTF('/models/camera.glb');
    const modelRef = useRef();
    const camRotateRefs = useRef([]);
    const flickerMeshesRef = useRef([]); // Changed to array for multiple meshes
    const redMeshesRef = useRef([]);
    const isSetup = useRef(false);

    const mouse = useRef({ x: 0, y: 0 });
    const targetRotation = useRef({ x: 0, y: 0 });

    const MAX_ROTATION_X = THREE.MathUtils.degToRad(15);
    const MAX_ROTATION_Y = THREE.MathUtils.degToRad(30);
    const SMOOTHING_FACTOR = 0.08;

    // Setup camera and scene
    useEffect(() => {
        if (!gltf?.scene || isSetup.current) return;
        isSetup.current = true;

        // Setup camera from CAM1
        const cam1 = gltf.scene.getObjectByName('CAM1');
        if (cam1 && cam1.isCamera) {
            gltf.scene.updateMatrixWorld(true);
            cam1.updateMatrixWorld(true);

            const position = new THREE.Vector3();
            const quaternion = new THREE.Quaternion();
            const scale = new THREE.Vector3();
            cam1.matrixWorld.decompose(position, quaternion, scale);

            camera.position.copy(position);
            camera.quaternion.copy(quaternion);
            camera.fov = cam1.fov || 50;
            camera.zoom = 1;
            camera.updateProjectionMatrix();
        }

        // Find cam_rotate objects and setup materials
        gltf.scene.traverse((object) => {
            if (object.name === 'cam_rotate' || /^cam_rotate\d+$/.test(object.name)) {
                camRotateRefs.current.push(object);
            }

            if (object.isMesh) {
                if (object.name.startsWith('mesh_flicker')) {
                    flickerMeshesRef.current.push(object); // Push to array
                    object.material = new THREE.MeshBasicMaterial({
                        color: new THREE.Color(1, 0, 0),
                        transparent: true,
                        opacity: 1,
                        side: THREE.DoubleSide,
                        depthWrite: false,
                        toneMapped: false
                    });
                    object.visible = true;
                    object.renderOrder = 999;
                }
                else if (object.material?.name === "red" || object.name.toLowerCase().includes("red")) {
                    object.material.emissive = new THREE.Color(1, 0, 0);
                    object.material.emissiveIntensity = 1;
                    redMeshesRef.current.push(object);
                } else if (object.material) {
                    object.material.emissive = new THREE.Color(0.05, 0.05, 0.1);
                    object.material.emissiveIntensity = 0.5;
                }
            }
        });

        onLoaded?.();
    }, [gltf, camera, onLoaded]);

    // Mouse tracking
    useEffect(() => {
        const handleMouseMove = (e) => {
            mouse.current.x = (e.clientX / window.innerWidth) * 2 - 1;
            mouse.current.y = -((e.clientY / window.innerHeight) * 2 - 1);
            targetRotation.current.y = mouse.current.x * MAX_ROTATION_Y;
            targetRotation.current.x = mouse.current.y * MAX_ROTATION_X;
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    // Animation
    useFrame((state) => {
        const now = state.clock.getElapsedTime();

        // Camera rotation
        camRotateRefs.current.forEach((rotObj) => {
            rotObj.rotation.x += (-targetRotation.current.x - rotObj.rotation.x) * SMOOTHING_FACTOR;
            rotObj.rotation.y += (targetRotation.current.y - rotObj.rotation.y) * SMOOTHING_FACTOR;
        });

        // Flicker for all meshes
        const blinkValue = Math.abs(Math.sin(now * 1));
        flickerMeshesRef.current.forEach((mesh) => {
            if (mesh?.material) {
                mesh.material.opacity = 0.5 + blinkValue * 1.5;
            }
        });

        // Red meshes
        const redBlinkValue = Math.abs(Math.sin(now * 2)) * 2;
        redMeshesRef.current.forEach((mesh) => {
            if (mesh.material) {
                mesh.material.emissiveIntensity = redBlinkValue;
            }
        });
    });

    return (
        <>
            <directionalLight
                position={[15, 10, 8]}
                intensity={0.2}
                color="#ffffff"
                castShadow
                shadow-mapSize={[512, 512]}
                shadow-camera-far={30}
                shadow-camera-left={-5}
                shadow-camera-right={5}
                shadow-camera-top={5}
                shadow-camera-bottom={-5}
            />

            <spotLight
                position={[-10, 0, 0]}
                angle={1}
                penumbra={0.5}
                intensity={2}
                color="#ffffff"
                decay={0}
            />

            <spotLight
                position={[-10, 0, 10]}
                angle={1}
                penumbra={0.5}
                intensity={1}
                color="#ffffff"
                decay={0}
            />

            <pointLight
                position={[-8, -3, 10]}
                intensity={1}
                color="#ff8844"
                decay={0}
            />

            <pointLight
                position={[0, -8, 2]}
                intensity={1}
                color="#ff0000"
                distance={25}
                decay={0}
            />

            <group ref={modelRef} position={[0, 0, 0]} scale={0.45}>
                <primitive object={gltf.scene} />
            </group>
        </>
    );
}

// Removed preload - load only when Contact section is visible
// useGLTF.preload('/models/camera.glb');

export default Camera3D;